import { useState, useEffect, useMemo, useRef } from "react";
import { Text, Box, Spinner, TextInput, Button, Divider } from "@okshaun/components";
import { css } from "@styled-system/css";
import { DashboardHeader } from "@/components/layout/header";
import { deleteMemory, updateMemory, type MemoryEntry } from "@/lib/api";
import { getCached, syncCache, clearCache, removeFromCache, replaceInCache, TAB_KEY } from "@/lib/memory-cache";
import { MemoryRow } from "@/components/memory-manager/memory-row";
import { Flex } from "@/styled-system/jsx";

type Tier = "T1" | "T2";

// Module-level in-memory cache — survives tab switches, no async needed.
const memCache = new Map<Tier, MemoryEntry[]>();

export function MemoriesPage() {
  const [tab, setTab] = useState<Tier>(() => {
    const saved = localStorage.getItem(TAB_KEY);
    return saved === "T2" ? "T2" : "T1";
  });
  const [filter, setFilter] = useState("");
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const effectId = useRef(0);

  useEffect(() => {
    const id = ++effectId.current;
    const stale = () => id !== effectId.current;
    setError(null);
    setSelectedIds(new Set());

    // Synchronous: check module-level in-memory cache first
    const mem = memCache.get(tab);
    if (mem) {
      setEntries(mem);
      setLoading(false);
      setSyncing(true);
    } else {
      setEntries([]);
      setLoading(true);
    }

    const run = async () => {
      try {
        // If no in-memory hit, try IDB before going to network
        if (!mem) {
          const fromIDB = await getCached(tab);
          if (stale()) return;
          if (fromIDB) {
            setEntries(fromIDB);
            setLoading(false);
            memCache.set(tab, fromIDB);
          }
        }

        // Background sync — spinner only shows if we had nothing to display
        const fresh = await syncCache(tab);
        if (stale()) return;
        setEntries(fresh);
        memCache.set(tab, fresh);
      } catch (err) {
        if (stale()) return;
        if (!memCache.has(tab)) {
          setError(err instanceof Error ? err.message : "Failed to load memories");
        }
      } finally {
        if (!stale()) {
          setLoading(false);
          setSyncing(false);
        }
      }
    };

    run();
  }, [tab]);

  const handleRefresh = async () => {
    memCache.delete(tab);
    await clearCache(tab);
    setEntries([]);
    setLoading(true);
    setError(null);
    try {
      const fresh = await syncCache(tab);
      setEntries(fresh);
      memCache.set(tab, fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.content.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [entries, filter]);

  const handleDelete = async (id: string) => {
    await deleteMemory(id, tab);
    await removeFromCache(tab, id);
    setEntries((prev) => {
      const next = prev.filter((e) => e.id !== id);
      memCache.set(tab, next);
      return next;
    });
  };

  const handleUpdate = async (id: string, text: string, tags: string[]) => {
    const newEntry = await updateMemory(id, text, tags, tab);
    await replaceInCache(tab, id, newEntry);
    setEntries((prev) => {
      const next = [newEntry, ...prev.filter((e) => e.id !== id)];
      memCache.set(tab, next);
      return next;
    });
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => deleteMemory(id, tab)));
      for (const id of ids) await removeFromCache(tab, id);
      setEntries((prev) => {
        const next = prev.filter((e) => !selectedIds.has(e.id));
        memCache.set(tab, next);
        return next;
      });
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const tabStyle = (active: boolean) =>
    css({
      textStyle: "mono.xs",
      px: "12",
      py: "8",
      cursor: "pointer",
      bg: "transparent",
      border: "none",
      borderBottom: active ? "[2px solid]" : "[2px solid transparent]",
      borderBottomColor: active ? "text.accent.purple" : "transparent",
      color: active ? "text" : "text.subtle",
      _hover: { color: "text" },
    });

  return (
    <Box
      display="flex"
      flexDir="column"
      minH="screen"
      maxW="[1600px]"
      mx="auto"
      pt="12"
      pb="20"
      px="20"
      gap="20"
    >
      <DashboardHeader />

      <Box mx="auto" w="full" maxW="[900px]">

        {/* Tabs + filter row */}
        <Box display="flex" alignItems="center" gap="0" borderBottom="default" mb="0">
          <button type="button" onClick={() => { setTab("T1"); localStorage.setItem(TAB_KEY, "T1"); }} className={tabStyle(tab === "T1")}>
            T1 — General
          </button>
          <button type="button" onClick={() => { setTab("T2"); localStorage.setItem(TAB_KEY, "T2"); }} className={tabStyle(tab === "T2")}>
            T2 — Private
          </button>
          <Box ml="auto" display="flex" alignItems="center" gap="8">
            <TextInput
              name="memory-filter"
              type="text"
              placeholder="Type to filter…"
              value={filter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
              fontFamily="mono"
              w="[240px]"
            />
            {filter && (
              <Button size="sm" variant="subtle" type="button" onClick={() => setFilter("")}>
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {/* Count + sync indicator */}
        {!loading && !error && (
          <Box display="flex" alignItems="center" gap="16" py="12" borderBottom="default">
            <Text textStyle="mono.xs" color="text.subtlest">
              {filtered.length === entries.length
                ? `${entries.length} entries`
                : `${filtered.length} of ${entries.length} entries`}
            </Text>
            {selectedIds.size >= 1 && (
              <Flex alignItems="center" gap="8">
                <Divider direction="vertical" weight="thin" borderColor="border" my="2" mr="8" />
                <Text textStyle="mono.xs" color="text.accent.purple">{selectedIds.size} selected</Text>
                <Button onClick={() => setSelectedIds(new Set())} size="sm" variant="hollow" type="button">Clear</Button>
                <Button onClick={() => setSelectedIds(new Set(filtered.map(e => e.id)))} size="sm" variant="hollow" type="button">Select All</Button>
              </Flex>
            )}
            {syncing && <Spinner size="sm" />}
            <Flex alignItems="center" ml="auto">
              {(selectedIds.size > 0) ? (
                <Button
                  size="sm"
                  variant="primary"
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  loading={bulkDeleting}
                >
                  Delete {selectedIds.size} {selectedIds.size === 1 ? "memory" : "memories"}
                </Button>
              ) : (
                <Button size="sm" variant="subtle" onClick={handleRefresh} disabled={loading}>
                  Refresh
                </Button>
              )}
            </Flex>
          </Box>
        )}

        {/* Body */}
        <Box bg="surface" border="default" borderTop="none" flex="1">
          {loading && (
            <Box display="flex" alignItems="center" gap="8" p="16">
              <Spinner size="sm" />
              <Text textStyle="mono.xs">Loading {tab} memories…</Text>
            </Box>
          )}

          {error && (
            <Box p="16">
              <Text textStyle="mono.xs" color="text.danger">{error}</Text>
            </Box>
          )}

          {!loading && !error && filtered.length === 0 && (
            <Box p="16">
              <Text textStyle="mono.xs" color="text.subtlest">
                {filter ? "No entries match your filter." : `No ${tab} memories yet.`}
              </Text>
            </Box>
          )}

          {!loading && !error && filtered.map((entry) => (
            <MemoryRow
              key={entry.id}
              entry={entry}
              highlight={filter}
              selected={selectedIds.has(entry.id)}
              onToggleSelect={handleToggleSelect}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
